import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaSave, FaTimes, FaTrash, FaSyncAlt } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import dayjs from "dayjs";
import Select from "react-select";

interface ShippingDetail {
  productId: string;
  productName: string;
  qty: number;
  pickingQty?: number;
  pickingTaskId: string;
}

interface ShippingOrder {
  id?: string;
  shippingNumber?: string;
  outboundOrderId: string;
  outboundOrderNo?: string;
  status?: string;
  shippingDate?: string;
  driverName?: string;
  vehicleNo?: string;
  notes?: string;
  createdAt?: string;
  createdBy?: string;
  details: ShippingDetail[];
}

interface OutboundOrderOption {
  value: string;
  label: string;
}

interface ProductOption {
  value: string;       // pickingTaskId
  label: string;       // productName
  pickingQty: number;
}

const ShippingOrdersForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isEdit = Boolean(id);
  const API = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState<ShippingOrder>({
    outboundOrderId: "",
    shippingDate: dayjs().format("YYYY-MM-DD"),
    details: [],
  });
  const [outboundOptions, setOutboundOptions] = useState<OutboundOrderOption[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isReadOnly = ["Submitted", "Shipped"].includes(formData.status || "");

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const fetchOutboundOptions = async () => {
    try {
      const res = await axios.get(`${API}/outbound-orders/with-completed-picking`, getAuthHeader());
      setOutboundOptions(
        (res.data || []).map((o: any) => ({
          value: o.id,
          label: o.outboundOrderNo || o.referenceNo || o.id,
        }))
      );
    } catch (err: any) {
      if (err.response?.status === 401) {
        logout();
        navigate("/login");
      }
    }
  };

  const fetchProductsByOutbound = async (outboundOrderId: string) => {
    if (!outboundOrderId) return;
    try {
      const res = await axios.get(`${API}/outbound-orders/${outboundOrderId}/picked-products`, getAuthHeader());
      const products: ProductOption[] = (res.data || []).map((p: any) => {
		  const pickingTaskId = p.picking_task_id ?? p.pickingTaskId ?? p.id;
		  return {
			value: pickingTaskId ? String(pickingTaskId) : "",
			label: p.product_name ?? p.productName ?? "",
			pickingQty: p.picked_qty ?? p.pickedQty ?? 0,
		  };
		});
      setProductOptions(products);

      // Auto-set details
      setFormData(prev => ({
        ...prev,
        details: products.map(p => ({
          productId: p.value,
          productName: p.label,
          qty: p.pickingQty,
          pickingQty: p.pickingQty,
          pickingTaskId: p.value,
        })),
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchShippingOrder = async () => {
    if (!isEdit) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/shipping-orders/${id}`, getAuthHeader());
      const data = res.data;
      setFormData({
        id: data.id,
        shippingNumber: data.shippingNumber,
        outboundOrderId: data.outboundOrder?.id || "",
        outboundOrderNo: data.outboundOrder?.outboundOrderNo || "",
        status: data.status,
        shippingDate: data.shippingDate || dayjs().format("YYYY-MM-DD"),
        driverName: data.driverName || "",
        vehicleNo: data.vehicleNo || "",
        notes: data.notes || "",
        createdAt: data.createdAt,
        createdBy: data.createdBy,
        details: data.details.map((d: any) => ({
          productId: d.picking_task_id,
          productName: d.product?.name || "",
          qty: d.qty,
          pickingQty: d.product?.pickedQty,
          pickingTaskId: d.picking_task_id,
        })),
      });
      if (data.outboundOrder?.id) fetchProductsByOutbound(data.outboundOrder.id);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOutboundOptions();
    fetchShippingOrder();
  }, [id]);

  const handleOutboundChange = (selected: OutboundOrderOption | null) => {
    if (!selected) return;
    setFormData(prev => ({
      ...prev,
      outboundOrderId: selected.value,
      outboundOrderNo: selected.label,
      details: [],
    }));
    fetchProductsByOutbound(selected.value);
  };

  const removeDetailItem = (index: number) => {
    const newDetails = [...formData.details];
    newDetails.splice(index, 1);
    setFormData(prev => ({ ...prev, details: newDetails }));
  };

  const refreshAllProducts = () => {
    if (!formData.outboundOrderId) return;
    fetchProductsByOutbound(formData.outboundOrderId);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.outboundOrderId) return alert("Please select Outbound Order.");
    if (!formData.shippingDate) return alert("Shipping Date is mandatory.");

    const payload = {
      outbound_order_id: formData.outboundOrderId,
      shipping_date: formData.shippingDate,
      driver_name: formData.driverName,
      vehicle_no: formData.vehicleNo,
      notes: formData.notes,
      details: formData.details
		  .filter(d => d.pickingTaskId && d.pickingTaskId.trim() !== "")
		  .map(d => ({
			picking_task_id: d.pickingTaskId,
			qty: d.qty,
		  })),
    };

    console.log(">>> Payload to POST /shipping-orders:", payload);

    if (payload.details.length === 0) return alert("No valid product to submit.");

    setLoading(true);
    try {
      if (isEdit)
        await axios.put(`${API}/shipping-orders/${id}`, payload, getAuthHeader());
      else
        await axios.post(`${API}/shipping-orders`, payload, getAuthHeader());

      navigate("/shipping-orders");
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-2 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{isEdit ? "Edit Shipping Order" : "Add Shipping Order"}</h2>
          <div className="flex gap-2">
            <button
              type="submit"
              form="shipping-form"
              className="bg-blue-600 text-white px-3 py-1 rounded flex items-center gap-1 text-sm"
              disabled={loading}
            >
              <FaSave /> Save
            </button>
            <button
              onClick={() => navigate("/shipping-orders")}
              className="bg-gray-300 text-gray-800 px-3 py-1 rounded flex items-center gap-1 text-sm"
            >
              <FaTimes /> Cancel
            </button>
          </div>
        </div>

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-400 rounded">
            {errorMessage}
          </div>
        )}

        <form id="shipping-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-2">
              <label className="w-36 text-sm font-medium">Shipping Number</label>
              <input
                type="text"
                value={formData.shippingNumber || ""}
                disabled
                className="flex-1 border rounded px-2 py-1 text-sm bg-gray-100"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="w-36 text-sm font-medium">
                Outbound Order <span>*</span>
              </label>
              <Select
                options={outboundOptions}
                value={
                  outboundOptions.find(o => o.value === formData.outboundOrderId) || null
                }
                onChange={handleOutboundChange}
                isDisabled={isReadOnly}
                placeholder="-- Select Outbound Order --"
                className="flex-1 text-sm"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="w-36 text-sm font-medium">Shipping Date</label>
              <input
                type="date"
                value={formData.shippingDate}
                onChange={e =>
                  setFormData(prev => ({ ...prev, shippingDate: e.target.value }))
                }
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="w-36 text-sm font-medium">Driver Name</label>
              <input
                type="text"
                value={formData.driverName || ""}
                onChange={e =>
                  setFormData(prev => ({ ...prev, driverName: e.target.value }))
                }
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="w-36 text-sm font-medium">Vehicle No</label>
              <input
                type="text"
                value={formData.vehicleNo || ""}
                onChange={e =>
                  setFormData(prev => ({ ...prev, vehicleNo: e.target.value }))
                }
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="w-36 text-sm font-medium">Notes</label>
              <input
                type="text"
                value={formData.notes || ""}
                onChange={e =>
                  setFormData(prev => ({ ...prev, notes: e.target.value }))
                }
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold">Products</h3>
              <button
                type="button"
                onClick={refreshAllProducts}
                className="text-blue-600 flex items-center gap-1 text-sm"
              >
                <FaSyncAlt /> Refresh All Products
              </button>
            </div>
            <table className="w-full border border-gray-200 rounded text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2 border">Product Name</th>
                  <th className="p-2 border">Qty</th>
                  <th className="p-2 border">Action</th>
                </tr>
              </thead>
              <tbody>
                {formData.details.map((d, idx) => (
                  <tr key={d.pickingTaskId}>
                    <td className="p-2 border">{d.productName}</td>
                    <td className="p-2 border">
                      <input
                        type="number"
                        value={d.qty}
                        min={1}
                        max={d.pickingQty}
                        onChange={e => {
                          const newDetails = [...formData.details];
                          newDetails[idx].qty = Number(e.target.value);
                          setFormData(prev => ({ ...prev, details: newDetails }));
                        }}
                        className="w-20 border rounded px-1 py-0.5 text-sm"
                      />
                    </td>
                    <td className="p-2 border text-center">
                      <button
                        type="button"
                        onClick={() => removeDetailItem(idx)}
                        className="text-red-600"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
                {formData.details.length === 0 && (
                  <tr>
                    <td colSpan={3} className="p-2 text-center text-gray-400">
                      No products added
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShippingOrdersForm;
